import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { PROJECTS } from "@/data/site";
import { SectionHead, FadeUp } from "@/components/site/Reveal";

const MotionLink = motion(Link);

const ProjectCard = ({ project, index }) => (
  <MotionLink
    to={`/realisations/${project.slug}`}
    initial={{ opacity: 0, y: 48 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.8, delay: (index % 2) * 0.12, ease: [0.22, 1, 0.36, 1] }}
    className={`group relative rounded-2xl overflow-hidden border border-white/[0.08] bg-[#0b0c1c] block transition-shadow duration-500 hover:shadow-[0_30px_80px_-24px_rgba(59,130,246,0.35)] ${
      project.size === "large" ? "md:col-span-7" : "md:col-span-5"
    }`}
    data-testid={`project-card-${project.slug}`}
  >
    <div className={`overflow-hidden ${project.size === "large" ? "aspect-[16/10]" : "aspect-[16/11]"}`}>
      <img
        src={project.image}
        alt={project.alt}
        className="w-full h-full object-cover object-top transition-transform duration-700 ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
        loading="lazy"
      />
    </div>
    <div className="absolute inset-0 bg-gradient-to-t from-[#03040b] via-[#03040b]/25 to-transparent opacity-90" />
    <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
      <p className="font-mono-label text-[10px] tracking-[0.25em] uppercase text-[var(--accent-cyan)] mb-2">
        {project.category}
      </p>
      <h3 className="font-display font-bold text-xl md:text-2xl tracking-tight">{project.name}</h3>
      <p className="text-sm text-[var(--text-secondary)] mt-2 max-w-md leading-relaxed opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0 hidden sm:block">
        {project.description}
      </p>
      <span className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-white/90 group-hover:text-[var(--accent-cyan)] transition-colors">
        Voir le projet <ArrowUpRight size={15} className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </span>
    </div>
  </MotionLink>
);

export const Realisations = () => (
  <section id="realisations" className="relative py-24 md:py-36" data-testid="realisations-section">
    <div className="glow-orb w-[500px] h-[500px] bg-violet-600/10 top-20 -right-60" />
    <div className="max-w-7xl mx-auto px-6 md:px-10">
      <SectionHead
        label="Réalisations"
        title={
          <>
            Des sites qui ne <span className="text-gradient">ressemblent pas</span> aux autres.
          </>
        }
        subtitle="Chaque création de site internet est pensée pour son activité, son image et ses clients. Un restaurant ne doit pas ressembler à un coiffeur — c'est justement notre différence. Cliquez pour les découvrir en ligne."
      />
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
        {PROJECTS.map((p, i) => (
          <ProjectCard key={p.slug} project={p} index={i} />
        ))}
      </div>
      <FadeUp className="mt-12 text-center">
        <p className="text-[var(--text-secondary)] text-sm font-mono-label tracking-[0.2em] uppercase">
          Peu importe votre métier — nous adaptons complètement le design à votre univers.
        </p>
      </FadeUp>
    </div>
  </section>
);

export default Realisations;
